import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, DatePicker, Input } from 'antd';

import fontService, { FontWeight } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { Colors, ICareerCertificateValue } from 'context';
import { Icon, IconNames } from 'atoms';
import { FormFieldset } from 'organisms/ResumeForm';

export interface IProfileCertificatesBlock {
  certificates: ICareerCertificateValue[];
  onChange: (certificates: ICareerCertificateValue[]) => void;
}

const emptyCertificate = (): ICareerCertificateValue => ({
  id: nanoid(),
  serverId: null,
  title: '',
  issuer: '',
  issuedAt: '',
  url: '',
  fileUrl: null,
});

/** Certificates section (spec 0018 §3.1). */
export const ProfileCertificatesBlock = memo(
  ({ certificates, onChange }: IProfileCertificatesBlock) => {
    const { t } = useTranslation();

    const patch = useCallback(
      (id: string, part: Partial<ICareerCertificateValue>) => {
        onChange(certificates.map((item) => (item.id === id ? { ...item, ...part } : item)));
      },
      [certificates, onChange],
    );

    const onRemove = useCallback(
      (id: string) => onChange(certificates.filter((item) => item.id !== id)),
      [certificates, onChange],
    );

    const onAdd = useCallback(
      () => onChange([...certificates, emptyCertificate()]),
      [certificates, onChange],
    );

    return (
      <FormFieldset groupTitle={t('careerProfile.certificates.title')}>
        <List>
          {certificates.map((certificate) => (
            <Card key={certificate.id}>
              <RemoveButton onClick={() => onRemove(certificate.id)}>
                <StyledCloseIcon name={IconNames.CloseSm} />
              </RemoveButton>

              <Label>{t('careerProfile.certificates.certTitle')}</Label>
              <Input
                value={certificate.title}
                maxLength={200}
                onChange={({ target: { value } }) => patch(certificate.id, { title: value })}
              />

              <TwoCol>
                <div>
                  <Label>{t('careerProfile.certificates.issuer')}</Label>
                  <Input
                    value={certificate.issuer}
                    onChange={({ target: { value } }) => patch(certificate.id, { issuer: value })}
                  />
                </div>
                <div>
                  <Label>{t('careerProfile.certificates.issuedAt')}</Label>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={certificate.issuedAt ? dayjs(certificate.issuedAt) : null}
                    onChange={(_, dateString) =>
                      patch(certificate.id, {
                        issuedAt: Array.isArray(dateString) ? dateString[0] : dateString,
                      })
                    }
                  />
                </div>
              </TwoCol>

              <Label>{t('careerProfile.certificates.url')}</Label>
              <Input
                value={certificate.url}
                onChange={({ target: { value } }) => patch(certificate.id, { url: value })}
              />
            </Card>
          ))}
        </List>

        <Button onClick={onAdd}>{t('careerProfile.certificates.addCertificate')}</Button>
      </FormFieldset>
    );
  },
);

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  position: relative;
  background-color: ${Colors.bg_gray};
  border-radius: 8px;
  padding: 20px 20px 28px;

  ${tablet} {
    padding: 24px;
  }

  ${desktop} {
    padding: 32px;
  }
`;

const Label = styled.p`
  margin: 16px 0 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}

  &:first-of-type {
    margin-top: 0;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${tablet} {
    grid-template-columns: 1fr 1fr;
  }
`;

const RemoveButton = styled.button`
  cursor: pointer;
  border-radius: 50%;
  border: none;
  background-color: ${Colors.white};
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 16px rgba(100, 100, 100, 0.2);
  position: absolute;
  top: -15px;
  right: -14px;
  z-index: 3;
  padding: 0;
`;

const StyledCloseIcon = styled(Icon)`
  width: 20px;
  height: 20px;
`;
